// Members of a Set_Draft (blueprint-sets design "components/SetMembersEditor.tsx"; Req 1.2, 1.4, 2.1, 2.5, 3.3, 3.4).
// A controlled editor: the set page owns the Member refs (`value`) and saves them; this component resolves each
// Member's name, Document_Type, and description at its ref, runs `validateSetDefinition` on every change, and shows
// its errors and warnings, plus the API's 400 `fieldErrors`, next to the Members they are about.
import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { z } from 'zod';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Modal from '@cloudscape-design/components/modal';
import Multiselect, { type MultiselectProps } from '@cloudscape-design/components/multiselect';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import {
  normalizeDocType,
  validateSetDefinition,
  type resolvedSetMemberSchema,
  type ResolvedMember,
  type SetMemberRef,
  type SetValidation,
  type ValidationErrors,
  type VersionRef,
} from '@bda/shared';
import { api, ApiError } from '../../api/client';
import { keys, useBlueprints } from '../../api/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { runtimeConfig } from '../../config';

/** A Member as `GET /sets/:id` resolved it. */
export type ResolvedSetMember = z.infer<typeof resolvedSetMemberSchema>;

/** Blocking member problems (keys `members`, `members.<i>`, `members.<i>.documentType`) and warnings of the value. */
export interface SetMembersValidation {
  errors: ValidationErrors;
  warnings: SetValidation['warnings'];
  /** Some Member is still loading or failed to load, so its rules are not checked yet: do not treat as valid. */
  pending: boolean;
}

export interface SetMembersEditorProps {
  /** The Set_Draft's Members, in order. */
  value: SetMemberRef[];
  onChange: (next: SetMemberRef[]) => void;
  /** Members from the set detail response; used for a Member whose blueprint and ref are unchanged (no extra reads). */
  resolved?: ResolvedSetMember[];
  /**
   * `details.fieldErrors` of the API's last 400 for this value. Member keys show at their rows; an error is hidden once
   * the Member it is about changes (another blueprint or ref), and `members` once any Member changes.
   */
  fieldErrors?: Record<string, string>;
  /** Also flag draft refs, as a publish does (Req 3.2). */
  forPublish?: boolean;
  /** Called with the member validation whenever it changes, so the page can block Save. */
  onValidationChange?: (v: SetMembersValidation) => void;
  /** Prefix of element ids, for more than one editor on a page. */
  idPrefix?: string;
}

/** A Member as the table shows it. */
export interface MemberRow {
  blueprintId: string;
  ref: VersionRef;
  name: string;
  documentType: string;
  description: string;
  exists: boolean;
  owned: boolean;
  archived: boolean;
  /** Latest published version of the blueprint (null: none or not known), for the version options. */
  latestVersion: number | null;
  /** Set when the ref is a number below the latest published version (Req 3.3). */
  newerVersion: number | null;
  /** `loading`/`error`: the blueprint or its version has not been read, so the rules skip it. */
  state: 'ready' | 'loading' | 'error';
}

export interface MemberIssues extends SetMembersValidation {
  /** Messages at `members` (count limits), shown above the table. */
  table: string[];
  /** Per row: blocking messages (own and conflicts it is part of) and warnings. */
  rows: Array<{ errors: string[]; warnings: string[] }>;
}

const MEMBER_KEY = /^members\.(\d+)(\.documentType)?$/;
const isMemberKey = (k: string): boolean => k === 'members' || MEMBER_KEY.test(k);

export const displayName = (r: Pick<MemberRow, 'name'>): string => r.name || 'Unknown blueprint';

/**
 * Runs `validateSetDefinition` on the rows (Req 2.1-2.5) and places each message at the rows it is about: a
 * Document_Type conflict at both Members (Req 2.1), a warning at every Member it names (Req 2.5). Rows that are not
 * `ready` take part as unique placeholders and their messages are dropped until they load. `apiErrors` (the API's
 * `fieldErrors`) fill keys the browser has no message for.
 */
export function memberIssues(
  rows: MemberRow[],
  opts: { maxMembers: number; forPublish: boolean; apiErrors?: Record<string, string> },
): MemberIssues {
  const ready = (i: number) => rows[i]!.state === 'ready';
  const members: ResolvedMember[] = rows.map((r, i) =>
    r.state === 'ready'
      ? {
          blueprintId: r.blueprintId,
          ref: r.ref,
          exists: r.exists,
          owned: r.owned,
          archived: r.archived,
          name: r.name,
          documentType: r.documentType,
          description: r.description,
        }
      : {
          blueprintId: r.blueprintId,
          ref: r.ref,
          exists: true,
          owned: true,
          archived: false,
          name: r.name,
          documentType: `\u0000pending ${i}`,
          description: `\u0000pending member ${i} placeholder description, long enough for no warning`,
        },
  );
  const v = validateSetDefinition({ name: 'members', description: '' }, members, {
    maxMembers: opts.maxMembers,
    forPublish: opts.forPublish,
  });
  const errors: ValidationErrors = {};
  for (const [k, msg] of Object.entries(v.errors)) {
    const m = MEMBER_KEY.exec(k);
    if (k === 'members' || (m && ready(Number(m[1])))) errors[k] = msg;
  }
  for (const [k, msg] of Object.entries(opts.apiErrors ?? {})) {
    const m = MEMBER_KEY.exec(k);
    if (isMemberKey(k) && !(k in errors) && (!m || Number(m[1]) < rows.length)) errors[k] = msg;
  }
  const warnings = v.warnings.filter((w) => w.members.every(ready));

  const out: MemberIssues['rows'] = rows.map(() => ({ errors: [], warnings: [] }));
  const add = (list: string[], msg: string) => {
    if (!list.includes(msg)) list.push(msg);
  };
  /** The earlier Member a Document_Type conflict at `j` is with: the first checked one with the same type. */
  const partner = (j: number): number => {
    const type = normalizeDocType(rows[j]!.documentType);
    return rows.findIndex(
      (r, i) =>
        i < j &&
        ready(i) &&
        r.exists &&
        r.owned &&
        rows.findIndex((x) => x.blueprintId === r.blueprintId) === i &&
        normalizeDocType(r.documentType) === type,
    );
  };
  const table: string[] = [];
  for (const [k, msg] of Object.entries(errors)) {
    const m = MEMBER_KEY.exec(k);
    if (!m) {
      table.push(msg);
      continue;
    }
    const j = Number(m[1]);
    add(out[j]!.errors, msg);
    if (m[2]) {
      const i = partner(j);
      if (i >= 0) add(out[i]!.errors, msg);
    }
  }
  for (const w of warnings) for (const i of w.members) add(out[i]!.warnings, w.message);
  return { errors, warnings, pending: rows.some((r) => r.state !== 'ready'), table, rows: out };
}

const sameRefs = (a: SetMemberRef | undefined, b: SetMemberRef | undefined): boolean =>
  !!a && !!b && a.blueprintId === b.blueprintId && a.ref === b.ref;

/** The API errors still about the current value: those of rows whose Member is unchanged since the 400. */
function currentApiErrors(
  errors: Record<string, string> | undefined,
  basis: SetMemberRef[],
  value: SetMemberRef[],
): Record<string, string> {
  if (!errors) return {};
  const unchanged = basis.length === value.length && value.every((m, i) => sameRefs(m, basis[i]));
  return Object.fromEntries(
    Object.entries(errors).filter(([k]) => {
      const m = MEMBER_KEY.exec(k);
      return m ? sameRefs(value[Number(m[1])], basis[Number(m[1])]) : k === 'members' && unchanged;
    }),
  );
}

const is404 = (e: unknown): boolean => e instanceof ApiError && e.status === 404;
const DRAFT_VALUE = 'DRAFT';
const refLabel = (ref: VersionRef): string => (ref === 'DRAFT' ? 'Current draft' : `Version ${ref}`);

/** Resolves every Member to a row: the author's blueprint list, the set detail, and reads of drafts and versions. */
function useMemberRows(value: SetMemberRef[], resolved: ResolvedSetMember[] | undefined) {
  const { user } = useAuth();
  const list = useBlueprints({ archived: 'false' });
  const listSettled = !list.isLoading;
  const byId = useMemo(() => new Map((list.data?.items ?? []).map((b) => [b.id, b])), [list.data]);
  const seedOf = (m: SetMemberRef) => resolved?.find((r) => sameRefs(r, m));

  // The blueprint detail: for a draft ref (its working schema) and for meta of blueprints not in the list (archived).
  const detailIds = [
    ...new Set(
      value
        .filter((m) => {
          const seed = seedOf(m);
          return (m.ref === 'DRAFT' && !seed) || (listSettled && !byId.has(m.blueprintId) && !seed);
        })
        .map((m) => m.blueprintId),
    ),
  ];
  const versionRefs = [
    ...new Map(
      value.filter((m) => m.ref !== 'DRAFT' && !seedOf(m)).map((m) => [`${m.blueprintId}#${m.ref}`, m]),
    ).values(),
  ] as Array<{ blueprintId: string; ref: number }>;
  const details = useQueries({
    queries: detailIds.map((id) => ({ queryKey: keys.blueprint(id), queryFn: () => api.getBlueprint(id) })),
  });
  const versions = useQueries({
    queries: versionRefs.map((m) => ({
      queryKey: keys.version(m.blueprintId, m.ref),
      queryFn: () => api.getVersion(m.blueprintId, m.ref),
    })),
  });
  const detailOf = (id: string) => details[detailIds.indexOf(id)];
  const versionOf = (id: string, ref: number) =>
    versions[versionRefs.findIndex((m) => m.blueprintId === id && m.ref === ref)];

  const rows: MemberRow[] = value.map((m) => {
    const seed = seedOf(m);
    const summary = byId.get(m.blueprintId);
    const detail = detailOf(m.blueprintId);
    const base: MemberRow = {
      blueprintId: m.blueprintId,
      ref: m.ref,
      name: summary?.name ?? seed?.name ?? '',
      documentType: '',
      description: '',
      exists: true,
      owned: true,
      archived: summary ? false : (seed?.archived ?? false),
      latestVersion: summary?.latestVersion ?? null,
      newerVersion: null,
      state: 'loading',
    };
    if (detail?.data) {
      const b = detail.data.blueprint;
      Object.assign(base, {
        name: b.name,
        archived: b.archived,
        latestVersion: b.latestVersion,
        owned: b.ownerSub === user?.sub,
      });
    } else if (!summary && seed) {
      base.latestVersion = seed.newerVersion ?? (typeof m.ref === 'number' ? m.ref : null);
    }
    if (detail?.isError) {
      return is404(detail.error) ? { ...base, exists: false, state: 'ready' } : { ...base, state: 'error' };
    }
    const metaKnown = !!summary || !!detail?.data || (!!seed && !detail);
    let content: Pick<MemberRow, 'exists' | 'documentType' | 'description'> | 'loading' | 'error' = 'loading';
    if (seed) content = { exists: seed.exists, documentType: seed.documentType, description: seed.description };
    else if (m.ref === 'DRAFT') {
      if (detail?.data)
        content = {
          exists: true,
          documentType: detail.data.blueprint.workingSchema.class,
          description: detail.data.blueprint.workingSchema.description,
        };
    } else {
      const q = versionOf(m.blueprintId, m.ref);
      if (q?.data)
        content = { exists: true, documentType: q.data.schema.class, description: q.data.schema.description };
      else if (q?.isError) content = is404(q.error) ? { exists: false, documentType: '', description: '' } : 'error';
    }
    if (content === 'error') return { ...base, state: 'error' };
    if (content === 'loading' || !metaKnown) return base;
    const newerVersion =
      typeof m.ref === 'number' && base.latestVersion !== null && base.latestVersion > m.ref
        ? base.latestVersion
        : null;
    return { ...base, ...content, newerVersion, state: 'ready' };
  });
  return { rows, list };
}

/** Members table with version picks, badges, errors, warnings, and "Add blueprints" (see the file comment). */
export function SetMembersEditor({
  value,
  onChange,
  resolved,
  fieldErrors,
  forPublish = false,
  onValidationChange,
  idPrefix = 'set-members',
}: SetMembersEditorProps) {
  const maxMembers = runtimeConfig().sets.maxSetMembers;
  const { rows, list } = useMemberRows(value, resolved);

  // The value the API errors were returned for: reset whenever a new fieldErrors object arrives.
  const [apiBasis, setApiBasis] = useState({ errors: fieldErrors, value });
  if (apiBasis.errors !== fieldErrors) setApiBasis({ errors: fieldErrors, value });
  const apiErrors = currentApiErrors(fieldErrors, apiBasis.value, value);

  const issues = memberIssues(rows, { maxMembers, forPublish, apiErrors });
  const report = JSON.stringify({ errors: issues.errors, warnings: issues.warnings, pending: issues.pending });
  useEffect(() => {
    onValidationChange?.(JSON.parse(report) as SetMembersValidation);
    // Only when the validation itself changes, not on every render of a new callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<MultiselectProps.Options>([]);
  const inSet = new Set(value.map((m) => m.blueprintId));
  const addOptions = (list.data?.items ?? [])
    .filter((b) => !b.archived && !inSet.has(b.id))
    .map((b) => ({
      value: b.id,
      label: b.name,
      description: b.documentType,
      tags: [b.latestVersion ? `Latest: version ${b.latestVersion}` : 'Not published'],
    }));
  const closeAdd = () => {
    setAdding(false);
    setPicked([]);
  };
  const addPicked = () => {
    const items = new Map((list.data?.items ?? []).map((b) => [b.id, b]));
    const added = picked.flatMap((o) => {
      const b = o.value ? items.get(o.value) : undefined;
      // Req 1.2: the latest published version is preselected; the draft when the blueprint was never published.
      return b ? [{ blueprintId: b.id, ref: (b.latestVersion ?? 'DRAFT') as VersionRef }] : [];
    });
    onChange([...value, ...added]);
    closeAdd();
  };

  const setRef = (i: number, ref: VersionRef) => onChange(value.map((m, j) => (j === i ? { ...m, ref } : m)));
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
  type Item = MemberRow & { index: number };
  const items: Item[] = rows.map((r, index) => ({ ...r, index }));

  const versionOptions = (r: MemberRow) => {
    const nums = new Set<number>();
    for (let v = r.latestVersion ?? 0; v >= 1; v--) nums.add(v);
    if (typeof r.ref === 'number') nums.add(r.ref);
    return [
      { value: DRAFT_VALUE, label: refLabel('DRAFT') },
      ...[...nums]
        .sort((a, b) => b - a)
        .map((v) => ({
          value: String(v),
          label: refLabel(v),
          description: v === r.latestVersion ? 'Latest published' : undefined,
        })),
    ];
  };

  const addButton = (
    <Button
      onClick={() => setAdding(true)}
      disabled={list.isLoading}
      disabledReason={list.isLoading ? 'Loading your blueprints.' : undefined}
    >
      Add blueprints
    </Button>
  );

  return (
    <SpaceBetween size="s">
      {issues.table.map((msg) => (
        <Box key={msg} color="text-status-error">
          <StatusIndicator type="error">{msg}</StatusIndicator>
        </Box>
      ))}
      <Table<Item>
        variant="container"
        trackBy={(r) => `${r.index}:${r.blueprintId}`}
        items={items}
        ariaLabels={{ tableLabel: 'Members' }}
        header={
          <Header
            variant="h2"
            counter={`(${value.length}/${maxMembers})`}
            description="Each blueprint must have its own document type. BDA matches each document in a packet to a blueprint by its description."
            actions={addButton}
          >
            Members
          </Header>
        }
        empty={
          <Box textAlign="center" color="inherit">
            <SpaceBetween size="xs">
              <Box>No blueprints in this set yet.</Box>
              {addButton}
            </SpaceBetween>
          </Box>
        }
        columnDefinitions={[
          { id: 'name', header: 'Blueprint', cell: (r) => displayName(r) },
          {
            id: 'documentType',
            header: 'Document type',
            cell: (r) =>
              r.state === 'loading' ? <StatusIndicator type="loading">Loading</StatusIndicator> : r.documentType || '-',
          },
          {
            id: 'version',
            header: 'Version',
            minWidth: 220,
            cell: (r) => {
              const options = versionOptions(r);
              const errors = issues.rows[r.index]!.errors;
              return (
                <FormField
                  stretch
                  errorText={
                    errors.length > 0 ? (
                      <SpaceBetween size="xxs">
                        {errors.map((e) => (
                          <span key={e}>{e}</span>
                        ))}
                      </SpaceBetween>
                    ) : undefined
                  }
                >
                  <Select
                    ariaLabel={`Version of ${displayName(r)}`}
                    selectedOption={options.find((o) => o.value === String(r.ref)) ?? null}
                    options={options}
                    onChange={(e) => {
                      const v = e.detail.selectedOption.value;
                      setRef(r.index, v === DRAFT_VALUE || v === undefined ? 'DRAFT' : Number(v));
                    }}
                  />
                </FormField>
              );
            },
          },
          {
            id: 'description',
            header: 'Description',
            maxWidth: 360,
            cell: (r) =>
              r.state === 'loading' ? '' : r.description || <Box color="text-status-inactive">No description</Box>,
          },
          {
            id: 'status',
            header: 'Status',
            minWidth: 220,
            cell: (r) => {
              const warnings = issues.rows[r.index]!.warnings;
              return (
                <SpaceBetween size="xxs">
                  {r.state === 'error' && (
                    <StatusIndicator type="error">Could not load this blueprint.</StatusIndicator>
                  )}
                  {r.archived && <Badge color="grey">Archived</Badge>}
                  {r.newerVersion !== null && (
                    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                      <Badge color="blue">Newer version available</Badge>
                      <Button
                        variant="inline-link"
                        ariaLabel={`Use version ${r.newerVersion} of ${displayName(r)}`}
                        onClick={() => setRef(r.index, r.newerVersion!)}
                      >
                        Use version {r.newerVersion}
                      </Button>
                    </SpaceBetween>
                  )}
                  {warnings.map((w) => (
                    <Box key={w} fontSize="body-s">
                      <StatusIndicator type="warning">{w}</StatusIndicator>{' '}
                      <Link
                        href={`/blueprints/${encodeURIComponent(r.blueprintId)}/edit`}
                        external
                        externalIconAriaLabel="(opens in a new tab)"
                        ariaLabel={`Edit blueprint ${displayName(r)}`}
                      >
                        Edit blueprint
                      </Link>
                    </Box>
                  ))}
                </SpaceBetween>
              );
            },
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (r) => (
              <Button
                variant="icon"
                iconName="remove"
                ariaLabel={`Remove ${displayName(r)}`}
                onClick={() => remove(r.index)}
              />
            ),
          },
        ]}
      />
      <Modal
        visible={adding}
        onDismiss={closeAdd}
        header="Add blueprints"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={closeAdd}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={picked.length === 0}
                disabledReason={picked.length === 0 ? 'Choose at least one blueprint.' : undefined}
                onClick={addPicked}
              >
                {picked.length === 0
                  ? 'Add'
                  : `Add ${picked.length} ${picked.length === 1 ? 'blueprint' : 'blueprints'}`}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <FormField
          label="Blueprints"
          description="Your blueprints that are not archived. Each is added at its latest published version, or its draft when it has none."
          controlId={`${idPrefix}-add`}
        >
          <Multiselect
            controlId={`${idPrefix}-add`}
            selectedOptions={picked}
            options={addOptions}
            onChange={(e) => setPicked(e.detail.selectedOptions)}
            placeholder="Choose blueprints"
            filteringType="auto"
            statusType={list.isLoading ? 'loading' : list.isError ? 'error' : 'finished'}
            loadingText="Loading your blueprints"
            errorText="Could not load your blueprints."
            empty="No other blueprints to add."
            tokenLimit={5}
          />
        </FormField>
      </Modal>
    </SpaceBetween>
  );
}
